import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';

const CompanyPage: React.FC = () => {
  const { theme } = useTheme();
  
  // Scroll to section when clicking internal links
  useEffect(() => {
    const handleSmoothScroll = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'A' && target.getAttribute('href')?.startsWith('#')) {
        e.preventDefault();
        const sectionId = target.getAttribute('href')?.substring(1);
        const section = document.getElementById(sectionId || '');
        section?.scrollIntoView({ behavior: 'smooth' });
      }
    };
    
    document.addEventListener('click', handleSmoothScroll);
    return () => document.removeEventListener('click', handleSmoothScroll);
  }, []);
  
  return (
    <div className={theme === 'dark' ? 'dark' : ''}>
      <div className="min-h-screen bg-background text-foreground">
        {/* Simple navigation header */}
        <header className="bg-card border-b border-border">
          <div className="max-w-6xl mx-auto px-6 py-4 flex justify-between items-center">
            <Link to="/" className="text-xl font-bold text-foreground hover:text-primary transition-colors">
              Giorgie
            </Link>
            <Link to="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
              Back to App
            </Link>
          </div>
        </header>
        
        <div className="max-w-4xl mx-auto px-6 py-16 md:py-20">
          
          {/* Hero Section */}
          <header className="text-center md:text-left mb-20 md:mb-24">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
              Meet Giorgie —<br className="hidden md:block" />
              <span className="text-primary">your personal AI concierge</span><br />
              for travel & beyond
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground mb-10 max-w-3xl mx-auto md:mx-0">
              Tell Giorgie what you love and get curated trips and weekend experiences in minutes, not hours.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
              <Link 
                to="/"
                className="inline-block px-8 py-4 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg font-semibold text-lg transition-all hover:scale-105 shadow-lg"
              >
                Try Giorgie Free
              </Link>
              <a 
                href="#how-it-works"
                className="inline-block px-8 py-4 text-primary hover:text-primary/80 font-medium text-lg transition-colors underline underline-offset-4"
              >
                Learn how it works
              </a>
            </div>
          </header>

          {/* Why We Built Giorgie */}
          <section className="mb-20 md:mb-24">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Why We Built Giorgie</h2>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-3xl">
              We believe planning memorable experiences shouldn't take hours of research. 
              Giorgie combines insider knowledge with advanced AI to create personalised 
              itineraries and local recommendations in minutes. It's like having a 
              well-travelled friend who listens to what you love and handles the details for you.
            </p>
          </section>

          {/* How It Works */}
          <section id="how-it-works" className="mb-20 md:mb-24 scroll-mt-20">
            <h2 className="text-3xl md:text-4xl font-bold mb-8">How It Works</h2>
            <div className="space-y-8">
              <div className="flex gap-6">
                <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                  <span className="text-primary font-bold text-xl">1</span>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Share your dream</h3>
                  <p className="text-lg text-muted-foreground">Chat with Giorgie about your ideal trip or weekend.</p>
                </div>
              </div>
              <div className="flex gap-6">
                <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                  <span className="text-primary font-bold text-xl">2</span>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Get instant itineraries</h3>
                  <p className="text-lg text-muted-foreground">Receive AI-crafted plans tailored to your preferences.</p>
                </div>
              </div>
              <div className="flex gap-6">
                <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                  <span className="text-primary font-bold text-xl">3</span>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Refine & enjoy</h3>
                  <p className="text-lg text-muted-foreground">Make tweaks and book with confidence — Giorgie keeps everything organised.</p>
                </div>
              </div>
            </div>
          </section>

          {/* Powered by AI */}
          <section className="mb-20 md:mb-24">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Powered by AI</h2>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-3xl">
              Giorgie is built on Claude, Anthropic's most advanced language model. 
              It understands context and preferences, adapting recommendations to your style. 
              The intelligence is invisible — you just see curated suggestions that feel 
              handpicked, without any talk about algorithms or data tracking.
            </p>
          </section>

          {/* Early User Feedback */}
          <section className="mb-20 md:mb-24">
            <h2 className="text-3xl md:text-4xl font-bold mb-8">Early User Feedback</h2>
            <div className="grid md:grid-cols-1 gap-8">
              <blockquote className="border-l-4 border-primary pl-6 py-2">
                <p className="text-lg md:text-xl italic text-muted-foreground mb-3">
                  "Giorgie planned our anniversary trip and suggested a hidden bistro we adored."
                </p>
                <cite className="text-sm font-medium text-muted-foreground/80">
                  — Sarah, Beta User
                </cite>
              </blockquote>
              
              <blockquote className="border-l-4 border-primary pl-6 py-2">
                <p className="text-lg md:text-xl italic text-muted-foreground mb-3">
                  "Finally, an app that gets what family-friendly means. Our kids loved every activity."
                </p>
                <cite className="text-sm font-medium text-muted-foreground/80">
                  — Mike, Beta User
                </cite>
              </blockquote>
              
              <blockquote className="border-l-4 border-primary pl-6 py-2">
                <p className="text-lg md:text-xl italic text-muted-foreground mb-3">
                  "Saved me hours of research for my Japan trip. The recommendations were spot-on."
                </p>
                <cite className="text-sm font-medium text-muted-foreground/80">
                  — Jennifer, Beta User
                </cite>
              </blockquote>
            </div>
          </section>
          
          {/* CTA Section - Repeat */}
          <section className="mb-20 md:mb-24 text-center bg-card rounded-lg p-8 md:p-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to start your journey?</h2>
            <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Join thousands discovering smarter travel planning with Giorgie.
            </p>
            <Link 
              to="/"
              className="inline-block px-8 py-4 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg font-semibold text-lg transition-all hover:scale-105 shadow-lg"
            >
              Try Giorgie Free
            </Link>
          </section>

          {/* Connect */}
          <footer className="border-t border-border pt-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Connect</h2>
            <p className="text-lg md:text-xl text-muted-foreground mb-6">
              Questions or feedback? Reach out at{' '}
              <a 
                href="mailto:hello@giorgie.ai"
                className="text-primary hover:text-primary/80 underline underline-offset-4 transition-colors"
              >
                hello@giorgie.ai
              </a>
            </p>
            <p className="text-lg text-muted-foreground">
              Follow us on{' '}
              <a 
                href="https://linkedin.com/company/giorgie"
                className="text-primary hover:text-primary/80 underline underline-offset-4 transition-colors"
                target="_blank"
                rel="noopener noreferrer"
              >
                LinkedIn
              </a>
              {' '}or{' '}
              <a 
                href="https://instagram.com/giorgie.ai"
                className="text-primary hover:text-primary/80 underline underline-offset-4 transition-colors"
                target="_blank"
                rel="noopener noreferrer"
              >
                Instagram
              </a>
              {' '}for travel inspiration.
            </p>
          </footer>
        </div>
      </div>
    </div>
  );
};

export default CompanyPage;